import { Helmet } from "react-helmet-async";
import {
  Alert,
  Card,
  CardActionArea,
  Chip,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectIsPreviewActive } from "../features/rolePreview/rolePreviewSlice";
import Loading from "../components/spinners/Loading";
import { useGetMemberEventsQuery } from "../features/events/eventsApiSlice";
import {
  formatDate,
  formatDateRange,
  formatCurrency,
  toLabel,
  getEventTypeColor,
  isTestingType,
  toDescriptionPreview,
  getEventPricingOptions,
  roundCurrency,
  EventMetaRow,
} from "./memberEventHelpers";

export default function MemberEventsPage() {
  const navigate = useNavigate();
  const isPreviewActive = useSelector(selectIsPreviewActive);
  const {
    data: events = [],
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetMemberEventsQuery();

  if (isLoading || isFetching) return <Loading />;

  if (isPreviewActive && isError && !events.length) {
    return (
      <Container maxWidth="sm" sx={{ mt: 6 }}>
        <Helmet>
          <title>MA | Veranstaltungen</title>
        </Helmet>
        <Alert severity="info" variant="outlined" sx={{ py: 2 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Keine Mitgliedsdaten vorhanden
          </Typography>
          <Typography variant="body2">
            Ihr Benutzerkonto ist nicht als Mitglied verknüpft. Die
            Mitgliedsansicht kann daher keine echten Vertragsdaten laden. Mit
            einem verknüpften Mitglied würden hier die verfügbaren
            Veranstaltungen erscheinen.
          </Typography>
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>Veranstaltungen | Mitgliederverwaltung</title>
      </Helmet>

      <Container maxWidth="md" sx={{ pb: 5 }}>
        {/* Seitenkopf */}
        <Stack spacing={0.5} sx={{ mb: 3, pt: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Icon icon="mdi:calendar-star" width={26} color="#1976d2" />
            <Typography variant="h5" fontWeight={700}>
              Veranstaltungen
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4.5 }}>
            Aktuelle Veranstaltungen, Prüfungen und Seminare deines Vereins.
          </Typography>
          {isError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error?.data?.message || "Fehler beim Laden der Veranstaltungen."}
            </Alert>
          )}
        </Stack>

        {/* Leerer Zustand */}
        {events.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 5, textAlign: "center", borderRadius: 2 }}>
            <Icon icon="mdi:calendar-remove-outline" width={44} color="#bbb" />
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1.5 }}>
              Aktuell sind keine öffentlichen Veranstaltungen verfügbar.
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={1.5}>
            {events.map((event) => {
              const eventId = String(event._id);
              const isTesting = isTestingType(event.eventType);
              const isBookingEvent = event.registrationType === "booking";

              // Testing members and RSVP counts
              const testingListMembers = isTesting
                ? (Array.isArray(event.contractMembersOnAttendeeList)
                    ? event.contractMembersOnAttendeeList
                    : [])
                : [];
              const testingZugesagtCount = testingListMembers.filter(
                (m) => m.rsvpStatus === "zugesagt",
              ).length;
              const testingAbgesagtCount = testingListMembers.filter(
                (m) => m.rsvpStatus === "abgesagt",
              ).length;
              const testingAusstehendCount = testingListMembers.filter(
                (m) => m.rsvpStatus === "ausstehend",
              ).length;

              const myRegistrationCount = (event.myParticipations || []).length;
              const descriptionPreview = toDescriptionPreview(event.description, 170);

              // Fee display for non-booking, fee-based events
              const eventPricingOpts = !isBookingEvent ? getEventPricingOptions(event) : [];
              const displayedFeeAmount =
                !isBookingEvent && event.isFeeBased
                  ? eventPricingOpts.length > 0
                    ? Math.min(...eventPricingOpts.map((o) => o.amount))
                    : roundCurrency(event.feeAmount)
                  : 0;

              return (
                <Card
                  key={eventId}
                  variant="outlined"
                  sx={{
                    borderRadius: 2.5,
                    borderColor:
                      isTesting && testingListMembers.length > 0
                        ? "primary.light"
                        : "divider",
                    transition: "border-color 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      borderColor: "primary.light",
                      boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
                    },
                  }}
                >
                  <CardActionArea
                    onClick={() => navigate(`/dashboard/veranstaltungen/${eventId}`)}
                    sx={{ p: 2 }}
                  >
                    <Stack spacing={1.25}>
                      <Stack
                        direction="row"
                        spacing={0.75}
                        flexWrap="wrap"
                        useFlexGap
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                          <Chip
                            label={toLabel(event.eventType)}
                            size="small"
                            color={getEventTypeColor(event.eventType)}
                            icon={
                              isTesting ? (
                                <Icon icon="mdi:medal" width={14} style={{ marginLeft: 6 }} />
                              ) : undefined
                            }
                          />
                          {isBookingEvent && (
                            <Chip
                              label="Buchungs-Event"
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          )}
                          {event.status === "abgesagt" && (
                            <Chip label="Abgesagt" size="small" color="error" />
                          )}
                          {event.status === "abgeschlossen" && (
                            <Chip label="Abgeschlossen" size="small" color="default" />
                          )}
                          {isTesting && testingListMembers.length > 0 && (
                            <Chip
                              label={`${testingListMembers.length} aufgestellt`}
                              size="small"
                              color="primary"
                              variant="outlined"
                              icon={
                                <Icon
                                  icon="mdi:account-star"
                                  width={14}
                                  style={{ marginLeft: 6 }}
                                />
                              }
                            />
                          )}
                          {isTesting && testingZugesagtCount > 0 && (
                            <Chip
                              label={`${testingZugesagtCount} zugesagt`}
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          )}
                          {isTesting && testingAbgesagtCount > 0 && (
                            <Chip
                              label={`${testingAbgesagtCount} abgesagt`}
                              size="small"
                              color="error"
                              variant="outlined"
                            />
                          )}
                          {isTesting && testingAusstehendCount > 0 && (
                            <Chip
                              label={`${testingAusstehendCount} ausstehend`}
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          )}
                          {!isTesting && !isBookingEvent && myRegistrationCount > 0 && (
                            <Chip
                              label={`${myRegistrationCount} angemeldet`}
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          )}
                        </Stack>
                        <Chip
                          label="Details"
                          size="small"
                          variant="outlined"
                          icon={
                            <Icon
                              icon="mdi:chevron-right"
                              width={14}
                              style={{ marginLeft: 6 }}
                            />
                          }
                        />
                      </Stack>

                      <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.35 }}>
                        {event.eventName || "Veranstaltung"}
                      </Typography>

                      <Stack spacing={0.6}>
                        <EventMetaRow icon="mdi:calendar-month">
                          {formatDateRange(event.eventDateBegin, event.eventDateEnd)}
                        </EventMetaRow>
                        {(event.locationName || event.city) && (
                          <EventMetaRow icon="mdi:map-marker">
                            {event.locationName || event.city}
                          </EventMetaRow>
                        )}
                        {!isTesting && !isBookingEvent && (
                          <EventMetaRow icon="mdi:account-group">
                            {event.participantCount || 0}
                            {event.maxParticipants ? ` / ${event.maxParticipants}` : ""} Plätze
                          </EventMetaRow>
                        )}
                        {!isBookingEvent && event.isFeeBased && (
                          <EventMetaRow icon="mdi:currency-eur" highlight>
                            Ab {formatCurrency(displayedFeeAmount)} pro Mitglied
                          </EventMetaRow>
                        )}
                        {isBookingEvent && event.registrationConfig?.registrationDeadline && (
                          <EventMetaRow icon="mdi:clock-outline">
                            Anmeldeschluss:{" "}
                            {formatDate(event.registrationConfig.registrationDeadline)}
                          </EventMetaRow>
                        )}
                      </Stack>

                      {descriptionPreview && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            lineHeight: 1.5,
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {descriptionPreview}
                        </Typography>
                      )}

                      <Typography variant="caption" color="primary.main" fontWeight={600}>
                        Details anzeigen
                      </Typography>
                    </Stack>
                  </CardActionArea>
                </Card>
              );
            })}
          </Stack>
        )}
      </Container>
    </>
  );
}
